import { DataLayer } from '@piwikpro/react-piwik-pro';
import React, { useMemo } from 'react';

import { graphql, Link, PageProps } from 'gatsby';

import { Grid2 as Grid, styled } from '@mui/material';
import { buttonClasses } from '@mui/material/Button';

import {
  ConsultationTeaser,
  ConversionButton,
  ResponsiveGrid,
  setARIATranslations,
} from '@mewa/ui';

import { compareServiceSegments } from '../../libs/data-sorting';
import t from '../../libs/translate';
import { useConversionButtonLabel } from '../a-b-testing/cta-label-experiment';
import {
  CustomerQuoteWrapper,
  StrapiCustomerReports,
} from '../components/customer-report/customer-quote-wrapper';
import {
  CustomerRatingsWrapper,
  StrapiCustomerRatingsData,
} from '../components/customer-report/customer-ratings-wrapper';
import { useFootnotes } from '../components/footer/footnotes';
import { StrapiFootnoteData } from '../components/footer/types';
import { NavigationData } from '../components/header/types';
import { ServiceSegmentsOverviewWrapper } from '../components/home/service-segments-overview-wrapper';
import { QuickNavigation } from '../components/quick-navigation/a-b-testing-variant';
import { trackAndNavigate } from '../components/quick-navigation/helper';
import { AwardData } from '../components/strapi/award';
import { StrapiMewaEssentials } from '../components/strapi/mewa-essentials/mewa-essentials';
import { MewaEssentialsData } from '../components/strapi/mewa-essentials/types';
import { StrapiStageImage } from '../components/strapi/stage-image';
import { GlobalPageContext } from '../types';
import {
  StrapiImage,
  StrapiResponsiveImageData,
} from './dynamic-content-page/content-components/image';

export { Head } from '../layouts/head';

type Props = NavigationData &
  StrapiCustomerReports &
  StrapiFootnoteData & {
    translations: Translations;
    strapiHomepage: {
      Stage: {
        headline: string;
        subHeadline: string;
        image: StrapiResponsiveImageData;
        award: AwardData;
      };
      mewaEssentials: MewaEssentialsData;
      campaignTeaser: {
        headline: string;
        linkLabel: string;
        campaignPage: {
          slug: string;
        } | null;
        image: StrapiResponsiveImageData;
      } | null;
      consultationTeaser: {
        headline: string;
        textblocks: {
          headline: string;
          text: string;
        }[];
        cta: {
          text: string;
          url: string;
        };
        image: StrapiResponsiveImageData;
      };
      customerRatings: StrapiCustomerRatingsData | null;
    };
  };

type PageContext = GlobalPageContext;

type Translations = {
  global_alarm: string;
  homepage_product_overview_headline: string;
  general_form_button_label_back: string;
  general_form_button_label_next: string;
  label_play: string;
  label_pause: string;
  show_products: string;
};

export const query = graphql`
  query ($marketRegex: String!, $lang: String!, $locale: String!) {
    ...NavigationFragment

    translations: translation(locale: { eq: $locale }) {
      global_alarm
      homepage_product_overview_headline
      general_form_button_label_back
      general_form_button_label_next
      label_play
      label_pause
      show_products
    }

    strapiHomepage(locale: { eq: $locale }) {
      Stage {
        headline: Headline
        subHeadline: SubHeadline
        image: Image {
          ...StrapiImageFragment
        }
        award: Award {
          ...AwardFragment
        }
      }

      mewaEssentials {
        ...MewaEssentialsFragment
      }

      campaignTeaser: CampaignTeaser {
        headline: Headline
        linkLabel: LinkLabel
        campaignPage: CampaignPage {
          slug: Slug
        }
        image: Image {
          ...StrapiImageFragment
        }
      }

      consultationTeaser {
        textblocks {
          headline
          text
        }
        headline
        cta {
          text
          url
        }
        image {
          ...StrapiImageFragment
        }
      }

      customerRatings {
        ...CustomerRatingsFragment
      }
    }

    ...CustomerReportFragment
    ...FootnotesFragment
  }
`;

export const Homepage = ({
  data: {
    navigationIndustries: { nodes: industries },
    navigationServiceSegments: { nodes: serviceSegments },
    navigationClothing: { nodes: clothingServiceSegments },
    navigationNonClothing: { nodes: nonClothingServiceSegments },
    navigationCleanRoom: { nodes: cleanroomServiceSegments },
    navigationLinks,
    strapiHomepage,
    translations,
    headerAndFooterTranslations,
    searchPageTranslations,
    customerReports,
    footnotes: { nodes: allFootnotes },
    customerQuoteTranslations,
  },
  pageContext: { langPrefix, market, lang, featureDev },
}: PageProps<Props, PageContext>) => {
  const footnoteReferences = useMemo(
    () =>
      strapiHomepage.customerRatings?.ratings.map(
        (r) => r.footnote.strapi_id,
      ) ?? [],
    [strapiHomepage.customerRatings],
  );
  const { footnoteIndexes } = useFootnotes(footnoteReferences, allFootnotes);

  const { ctaLabel } = useConversionButtonLabel({
    translations: headerAndFooterTranslations,
  });

  const trackButtonEvent = (trackingEventName: string) => {
    DataLayer.push({ event: trackingEventName });
  };

  const alarmText = t(translations, 'global_alarm');

  const { consultationTeaser } = strapiHomepage;

  setARIATranslations({
    label_back: translations.general_form_button_label_back,
    label_next: translations.general_form_button_label_next,
    label_pause: translations.label_pause,
    label_play: translations.label_play,
  });

  return (
    <>
      {/* This text is required in the CMS, but we want to hide the alarm in some markets.
      In that case, we need to use the string 'null'  */}
      {alarmText && alarmText !== 'null' ? (
        <ResponsiveGrid sx={{ background: 'var(--color-light-red)' }}>
          <Grid size={12}>
            <GlobalAlarm
              dangerouslySetInnerHTML={{ __html: alarmText }}
            ></GlobalAlarm>
          </Grid>
        </ResponsiveGrid>
      ) : null}
      <StrapiStageImage
        data={{
          headline: strapiHomepage.Stage.headline,
          subline: strapiHomepage.Stage.subHeadline,
          image: strapiHomepage.Stage.image,
        }}
      >
        <QuickNavigation
          langPrefix={langPrefix}
          industries={industries}
          serviceSegments={serviceSegments.sort((s1, s2) =>
            compareServiceSegments(s1.key, s2.key),
          )}
          translations={{
            quickNavHeadline: t(
              headerAndFooterTranslations,
              'quicknav_headline',
            ),
            quickNavAllIndustries: t(
              headerAndFooterTranslations,
              'quicknav_all_industries',
            ),
            quickNavAlProducts: t(
              headerAndFooterTranslations,
              'quicknav_all_products',
            ),
            quickNavIndustryPlaceholder: t(
              headerAndFooterTranslations,
              'quicknav_industry_placeholder',
            ),
            quickNavProductsPlaceholder: t(
              headerAndFooterTranslations,
              'quicknav_products_placeholder',
            ),
            search: t(searchPageTranslations, 'general_search'),
            showProducts: t(translations, 'show_products'),
          }}
          productOverviewSlug={navigationLinks.navigation.productMenu.slug}
          nonClothingSlug={navigationLinks.navigation.nonClothing.slug}
          cleanroomSlug={navigationLinks.navigation.cleanroom.slug}
          onSearch={(urlWithoutLangPrefix: string) => {
            trackAndNavigate(`${langPrefix}/${urlWithoutLangPrefix}`);
          }}
          featureDev={featureDev}
          navigationLinks={navigationLinks}
        />
      </StrapiStageImage>

      <StrapiMewaEssentials
        lang={lang}
        pressOverviewSlug={`${langPrefix}/${navigationLinks.pressOverview.slug}`}
        mewaEssentials={strapiHomepage.mewaEssentials}
      ></StrapiMewaEssentials>

      <ResponsiveGrid sx={{ display: [null, 'none'] }}>
        <Grid size={12} sx={{ marginBottom: 'var(--spacer-xxxl)' }}>
          <ConversionButton
            variant="contained"
            display={['inline-flex', 'none']}
            sx={{
              marginTop: '60px',

              [`&.${buttonClasses.outlined}`]: {
                paddingTop: '18px',
                paddingBottom: '20px',
              },
            }}
            link={{
              component: Link,
              props: {
                to: `${langPrefix}/${navigationLinks.conversion.contact.slug}`,
              },
            }}
          >
            {ctaLabel}
          </ConversionButton>
        </Grid>
      </ResponsiveGrid>
      {consultationTeaser && (
        <ResponsiveGrid>
          <Grid size={{ xs: 12, md: 10 }} offset={{ xs: 0, md: 1 }}>
            <ConsultationTeaser
              image={<StrapiImage responsiveImage={consultationTeaser.image} />}
              headline={consultationTeaser.headline}
              textblocks={consultationTeaser.textblocks}
              link={{
                component: Link,
                props: {
                  to: `${langPrefix}/${navigationLinks.conversion.contact.slug}`,
                },
              }}
              linkLabel={consultationTeaser.cta.text}
              onClick={() =>
                trackButtonEvent('cta_homepage_consultation_contact')
              }
            />
          </Grid>
        </ResponsiveGrid>
      )}

      <ServiceSegmentsWrapper>
        <ServiceSegmentsOverviewWrapper
          clothingServiceSegments={clothingServiceSegments}
          nonClothingServiceSegments={nonClothingServiceSegments}
          cleanroomServiceSegments={cleanroomServiceSegments}
          langPrefix={langPrefix}
          headerAndFooterTranslations={headerAndFooterTranslations}
          navigationLinks={navigationLinks}
          headline={t(translations, 'homepage_product_overview_headline')}
          featureDev={featureDev}
        />
      </ServiceSegmentsWrapper>
      <ResponsiveGrid>
        <Grid size={{ xs: 12, lg: 10 }} offset={{ xs: 0, lg: 1 }}>
          {strapiHomepage.customerRatings ? (
            <StyledCustomerRatingsWrapper
              {...strapiHomepage.customerRatings}
              footnoteIndexes={footnoteIndexes}
            />
          ) : null}
        </Grid>
        <Grid size={{ xs: 12, sm: 10 }} offset={{ xs: 0, sm: 1 }}>
          <CustomerQuoteWrapper
            customerReports={customerReports.nodes}
            headline={t(customerQuoteTranslations, 'customer_quote_headline')}
          />
        </Grid>
      </ResponsiveGrid>
    </>
  );
};

export default Homepage;

const GlobalAlarm = styled('div')(({ theme }) => ({
  ...theme.typography.h6,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  padding: '12px 0',
  color: 'white',

  a: {
    color: 'var(--color-white)',
    textDecoration: 'underline',
    textUnderlineOffset: '5px',
    textDecorationColor: 'transparent',
    transition:
      'color var(--transition-duration), text-decoration-color var(--transition-duration)',

    ':hover': {
      textDecorationColor: 'var(--color-white)',
    },
  },
}));

const ServiceSegmentsWrapper = styled('div')(({ theme }) => ({
  marginTop: '50px',

  [theme.breakpoints.up('sm')]: {
    marginTop: '60px',
  },

  [theme.breakpoints.up('md')]: {
    marginTop: '80px',
  },

  [theme.breakpoints.up('lg')]: {
    marginTop: '100px',
  },
}));

const StyledCustomerRatingsWrapper = styled(CustomerRatingsWrapper)({
  marginTop: 'var(--spacer-xxxl)',
});
